import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import type {
  RelationshipHistoryEntry,
  SpousePartnerEdge,
} from '@/lib/familyUnits';

import {
  canonicalTreeEdgePairKey,
  type CanonicalTreeEdgeStatus,
  type CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

import {
  validateCanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdgeAuthority';

import {
  adaptRelationshipWorkflowsToCanonicalEdges,
} from './CanonicalTreeRelationshipWorkflowAdapter';

export interface CanonicalCompatibilityEvidence {
  readonly source:
    CanonicalTreeRelationshipEdge['source'];
  readonly sourceReferenceId: string | null;
  readonly memberIds: readonly string[];
  readonly reason: string;
}

export interface CanonicalCompatibilitySourceCounts {
  readonly parentIds: number;
  readonly spouseIds: number;
  readonly relationshipWorkflows: number;
  readonly spousePartnerEdges: number;
  readonly relationshipHistory: number;
}

export interface CanonicalTreeCompatibilityProjection {
  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];
  readonly invalidEvidence:
    readonly CanonicalCompatibilityEvidence[];
  readonly unresolvedEvidence:
    readonly CanonicalCompatibilityEvidence[];
  readonly sourceCounts:
    CanonicalCompatibilitySourceCounts;
  readonly duplicateCount: number;
}

export interface CanonicalTreeCompatibilityProjectionInput {
  readonly members: readonly GraphMember[];
  readonly spousePartnerEdges?:
    readonly SpousePartnerEdge[];
  readonly relationshipHistory?:
    readonly RelationshipHistoryEntry[];
}

const statusFromText = (
  value: string | null | undefined,
  isCurrent?: boolean,
): CanonicalTreeEdgeStatus => {
  const normalized =
    (value ?? '').trim().toLowerCase();

  if (
    normalized === 'divorced' ||
    normalized === 'ex_spouse' ||
    normalized === 'ex-spouse'
  ) {
    return 'divorced';
  }

  if (
    normalized === 'widowed' ||
    normalized === 'deceased_spouse' ||
    normalized === 'deceased-spouse'
  ) {
    return 'widowed';
  }

  if (
    normalized === 'former' ||
    normalized === 'ended'
  ) {
    return 'former';
  }

  if (
    normalized === 'historical'
  ) {
    return 'historical';
  }

  if (
    normalized === 'separated'
  ) {
    return 'separated';
  }

  if (
    normalized === 'pending'
  ) {
    return 'pending';
  }

  if (
    normalized === 'disputed'
  ) {
    return 'disputed';
  }

  return isCurrent === false
    ? 'former'
    : 'active';
};

const confirmationForMember = (
  member: GraphMember,
): CanonicalTreeRelationshipEdge['confirmation'] =>
  member.approvalStatus === 'approved'
    ? 'confirmed'
    : member.approvalStatus === 'rejected'
      ? 'rejected'
      : 'pending';

const freezeEdge = (
  edge: CanonicalTreeRelationshipEdge,
): CanonicalTreeRelationshipEdge =>
  Object.freeze({
    ...edge,
    sharedChildIds:
      Object.freeze([...edge.sharedChildIds]),
    metadata:
      Object.freeze({ ...edge.metadata }),
  });

const sourcePriority:
  Readonly<Record<
    CanonicalTreeRelationshipEdge['source'],
    number
  >> = Object.freeze({
    'canonical-edge': 0,
    relationship_history: 1,
    spouse_partner_edges: 2,
    relationship_workflow: 3,
    spouse_id: 4,
    parent_ids: 5,
  });

const compatibilityIdentityKey = (
  edge: CanonicalTreeRelationshipEdge,
): string => {
  const endpoints =
    edge.direction === 'directed'
      ? `${edge.sourceMemberId}->${edge.targetMemberId}`
      : canonicalTreeEdgePairKey(
          edge.sourceMemberId,
          edge.targetMemberId,
        );

  return [
    edge.kind,
    edge.direction,
    endpoints,
    edge.status,
    edge.startedAt ?? '',
    edge.endedAt ?? '',
    [...edge.sharedChildIds].sort().join(','),
  ].join('|');
};

const adaptParentIds = (
  members: readonly GraphMember[],
  memberById: ReadonlyMap<string, GraphMember>,
  unresolvedEvidence: CanonicalCompatibilityEvidence[],
): CanonicalTreeRelationshipEdge[] => {
  const edges: CanonicalTreeRelationshipEdge[] = [];

  for (const child of members) {
    for (const parentId of child.parentIds ?? []) {
      const parent = memberById.get(parentId);

      if (!parent) {
        unresolvedEvidence.push({
          source: 'parent_ids',
          sourceReferenceId: child.id,
          memberIds: Object.freeze([
            child.id,
            parentId,
          ]),
          reason:
            'parent_ids references a member that is not present in the loaded graph.',
        });
        continue;
      }

      edges.push(
        freezeEdge({
          id:
            `parent_ids:${parent.id}:${child.id}`,

          ownerUserId:
            child.ownerUserId ?? parent.ownerUserId ?? null,

          sourceMemberId:
            parent.id,

          targetMemberId:
            child.id,

          kind:
            'parent-child',

          direction:
            'directed',

          status:
            'active',

          sharedChildIds:
            [],

          createsAncestry:
            true,

          inheritsGeneration:
            true,

          inheritsBranch:
            true,

          inheritsSide:
            true,

          createsInLawship:
            false,

          rendersActivePartnership:
            false,

          affiliatedBranch:
            false,

          startedAt:
            null,

          endedAt:
            null,

          confirmation:
            confirmationForMember(child),

          visibility:
            'family',

          preferredFamilyTitle:
            child.relationshipOverride ?? null,

          source:
            'parent_ids',

          sourceReferenceId:
            child.id,

          metadata: {
            childRelationship:
              child.relationship,
            childSide:
              child.side,
          },
        }),
      );
    }
  }

  return edges;
};

const adaptCoParentsFromParentIds = (
  members: readonly GraphMember[],
  memberById: ReadonlyMap<string, GraphMember>,
): CanonicalTreeRelationshipEdge[] => {
  const sharedChildrenByPair =
    new Map<
      string,
      {
        readonly parentAId: string;
        readonly parentBId: string;
        readonly childIds: Set<string>;
        readonly ownerUserId: string | null;
      }
    >();

  for (const child of members) {
    if (child.approvalStatus === 'rejected') continue;

    const parentIds = Array.from(
      new Set(
        (child.parentIds ?? [])
          .filter(parentId => {
            const parent = memberById.get(parentId);
            return Boolean(
              parent &&
              parent.approvalStatus !== 'rejected' &&
              parent.id !== child.id,
            );
          }),
      ),
    ).sort();

    if (parentIds.length < 2) continue;

    for (let leftIndex = 0; leftIndex < parentIds.length - 1; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < parentIds.length; rightIndex += 1) {
        const parentAId = parentIds[leftIndex];
        const parentBId = parentIds[rightIndex];
        const pairKey = canonicalTreeEdgePairKey(parentAId, parentBId);
        const existing = sharedChildrenByPair.get(pairKey);

        if (existing) {
          existing.childIds.add(child.id);
          continue;
        }

        sharedChildrenByPair.set(pairKey, {
          parentAId,
          parentBId,
          childIds: new Set([child.id]),
          ownerUserId:
            child.ownerUserId ??
            memberById.get(parentAId)?.ownerUserId ??
            memberById.get(parentBId)?.ownerUserId ??
            null,
        });
      }
    }
  }

  return [...sharedChildrenByPair.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([pairKey, evidence]) =>
      freezeEdge({
        id: `canonical-coparent:${pairKey}`,
        ownerUserId: evidence.ownerUserId,
        sourceMemberId: evidence.parentAId,
        targetMemberId: evidence.parentBId,
        kind: 'co-parent',
        direction: 'child-mediated',
        status: 'active',
        sharedChildIds: [...evidence.childIds].sort(),
        createsAncestry: false,
        inheritsGeneration: true,
        inheritsBranch: true,
        inheritsSide: true,
        createsInLawship: false,
        rendersActivePartnership: false,
        affiliatedBranch: false,
        startedAt: null,
        endedAt: null,
        confirmation: 'confirmed',
        visibility: 'family',
        preferredFamilyTitle: 'Co-Parent',
        source: 'canonical-edge',
        sourceReferenceId: pairKey,
        metadata: {
          derivedFromSharedParentage: true,
          sharedChildCount: evidence.childIds.size,
        },
      }),
    );
};

const adaptSpouseIds = (
  members: readonly GraphMember[],
  memberById: ReadonlyMap<string, GraphMember>,
  unresolvedEvidence: CanonicalCompatibilityEvidence[],
): CanonicalTreeRelationshipEdge[] => {
  const edges: CanonicalTreeRelationshipEdge[] = [];
  const seenPairs = new Set<string>();

  for (const member of members) {
    if (!member.spouseId) continue;

    const partner =
      memberById.get(member.spouseId);

    if (!partner) {
      unresolvedEvidence.push({
        source: 'spouse_id',
        sourceReferenceId: member.id,
        memberIds: Object.freeze([
          member.id,
          member.spouseId,
        ]),
        reason:
          'spouse_id references a member that is not present in the loaded graph.',
      });
      continue;
    }

    if (partner.id === member.id) {
      unresolvedEvidence.push({
        source: 'spouse_id',
        sourceReferenceId: member.id,
        memberIds: Object.freeze([
          member.id,
        ]),
        reason:
          'spouse_id contains a self-reference.',
      });
      continue;
    }

    const pair =
      [member.id, partner.id]
        .sort()
        .join('::');

    if (seenPairs.has(pair)) continue;
    seenPairs.add(pair);

    const reciprocal =
      partner.spouseId === member.id;

    edges.push(
      freezeEdge({
        id:
          `spouse_id:${pair}`,

        ownerUserId:
          member.ownerUserId ??
          partner.ownerUserId ??
          null,

        sourceMemberId:
          member.id,

        targetMemberId:
          partner.id,

        kind:
          'spouse-partner',

        direction:
          'symmetric',

        status:
          reciprocal
            ? 'active'
            : 'former',

        sharedChildIds:
          [],

        createsAncestry:
          false,

        inheritsGeneration:
          true,

        inheritsBranch:
          true,

        inheritsSide:
          true,

        createsInLawship:
          true,

        rendersActivePartnership:
          reciprocal,

        affiliatedBranch:
          false,

        startedAt:
          null,

        endedAt:
          null,

        confirmation:
          reciprocal
            ? 'confirmed'
            : 'pending',

        visibility:
          'family',

        preferredFamilyTitle:
          reciprocal
            ? 'Spouse / Partner'
            : 'Former or unconfirmed partner',

        source:
          'spouse_id',

        sourceReferenceId:
          member.id,

        metadata: {
          reciprocal,
          forwardMemberId:
            member.id,
        },
      }),
    );
  }

  return edges;
};

const metadataStringIds = (
  metadata: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): string[] => {
  const ids: string[] = [];

  for (const key of keys) {
    const value = metadata[key];

    if (typeof value === 'string' && value.trim()) {
      ids.push(value.trim());
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) {
          ids.push(item.trim());
        }
      }
    }
  }

  return Array.from(new Set(ids));
};

const sharedChildIdsFromMetadata = (
  metadata: Readonly<Record<string, unknown>>,
): string[] =>
  metadataStringIds(metadata, [
    'sharedChildIds',
    'shared_child_ids',
    'sharedChildId',
    'shared_child_id',
    'childIds',
    'child_ids',
    'childId',
    'child_id',
  ]);

const adaptSpousePartnerEdges = (
  records: readonly SpousePartnerEdge[],
  memberById: ReadonlyMap<string, GraphMember>,
  unresolvedEvidence: CanonicalCompatibilityEvidence[],
): CanonicalTreeRelationshipEdge[] => {
  const edges: CanonicalTreeRelationshipEdge[] = [];

  for (const record of records) {
    const personA = memberById.get(record.personAId);
    const personB = memberById.get(record.personBId);

    if (!personA || !personB) {
      unresolvedEvidence.push({
        source: 'spouse_partner_edges',
        sourceReferenceId: record.id || null,
        memberIds: Object.freeze([
          record.personAId,
          record.personBId,
        ]),
        reason:
          'Partnership edge references an endpoint that is not present in the loaded graph.',
      });
      continue;
    }

    if (personA.id === personB.id) {
      unresolvedEvidence.push({
        source: 'spouse_partner_edges',
        sourceReferenceId: record.id || null,
        memberIds: Object.freeze([personA.id]),
        reason:
          'Partnership edge contains a self-reference.',
      });
      continue;
    }

    const sharedChildIds =
      sharedChildIdsFromMetadata(record.metadata);

    const partnershipType =
      ['married', 'partner', 'fiance'].includes(
        record.unitType,
      );

    const historicalPartnershipType =
      [
        'divorced',
        'ex_spouse',
        'deceased_spouse',
      ].includes(record.unitType);

    if (partnershipType || historicalPartnershipType) {
      const status =
        record.unitType === 'divorced' ||
        record.unitType === 'ex_spouse'
          ? 'divorced'
          : record.unitType === 'deceased_spouse'
            ? 'widowed'
            : statusFromText(
                record.relationshipStatus,
                record.isCurrent,
              );

      edges.push(
        freezeEdge({
          id:
            record.id ||
            `spouse_partner_edges:${personA.id}:${personB.id}`,

          ownerUserId:
            record.ownerUserId ??
            personA.ownerUserId ??
            personB.ownerUserId ??
            null,

          sourceMemberId:
            personA.id,

          targetMemberId:
            personB.id,

          kind:
            'spouse-partner',

          direction:
            'symmetric',

          status,

          sharedChildIds,

          createsAncestry:
            false,

          inheritsGeneration:
            true,

          inheritsBranch:
            true,

          inheritsSide:
            true,

          createsInLawship:
            true,

          rendersActivePartnership:
            status === 'active' &&
            record.isCurrent,

          affiliatedBranch:
            false,

          startedAt:
            record.startedAt,

          endedAt:
            record.endedAt,

          confirmation:
            'confirmed',

          visibility:
            'family',

          preferredFamilyTitle:
            record.unitType === 'fiance'
              ? 'Fiancé / Fiancée'
              : record.unitType === 'partner'
                ? 'Partner'
                : record.unitType === 'deceased_spouse'
                  ? 'Deceased Spouse'
                  : historicalPartnershipType
                    ? 'Former Spouse'
                    : 'Spouse',

          source:
            'spouse_partner_edges',

          sourceReferenceId:
            record.id || null,

          metadata: {
            ...record.metadata,
            unitType:
              record.unitType,
            relationshipStatus:
              record.relationshipStatus,
            isCurrent:
              record.isCurrent,
            notes:
              record.notes,
          },
        }),
      );

      continue;
    }

    if (
      record.unitType === 'coparent' &&
      sharedChildIds.length > 0
    ) {
      edges.push(
        freezeEdge({
          id:
            record.id ||
            `spouse_partner_edges:${personA.id}:${personB.id}:coparent`,

          ownerUserId:
            record.ownerUserId ??
            personA.ownerUserId ??
            personB.ownerUserId ??
            null,

          sourceMemberId:
            personA.id,

          targetMemberId:
            personB.id,

          kind:
            'co-parent',

          direction:
            'child-mediated',

          status:
            statusFromText(
              record.relationshipStatus,
              record.isCurrent,
            ),

          sharedChildIds,

          createsAncestry:
            false,

          inheritsGeneration:
            true,

          inheritsBranch:
            true,

          inheritsSide:
            true,

          createsInLawship:
            false,

          rendersActivePartnership:
            false,

          affiliatedBranch:
            false,

          startedAt:
            record.startedAt,

          endedAt:
            record.endedAt,

          confirmation:
            'confirmed',

          visibility:
            'family',

          preferredFamilyTitle:
            'Co-Parent',

          source:
            'spouse_partner_edges',

          sourceReferenceId:
            record.id || null,

          metadata: {
            ...record.metadata,
            unitType:
              record.unitType,
            relationshipStatus:
              record.relationshipStatus,
            isCurrent:
              record.isCurrent,
            notes:
              record.notes,
          },
        }),
      );

      continue;
    }

    unresolvedEvidence.push({
      source: 'spouse_partner_edges',
      sourceReferenceId: record.id || null,
      memberIds: Object.freeze([
        personA.id,
        personB.id,
        ...sharedChildIds,
      ]),
      reason:
        record.unitType === 'coparent'
          ? 'Co-parent partnership evidence requires at least one governed shared-child connector.'
          : `Family unit type ${record.unitType} requires relationship-specific connector evidence before canonical projection.`,
    });
  }

  return edges;
};

const historyKindToCanonicalKind = (
  relationshipKind: string,
): CanonicalTreeRelationshipEdge['kind'] | null => {
  const normalized =
    relationshipKind.trim().toLowerCase();

  if (
    [
      'married',
      'marriage',
      'spouse',
      'partner',
      'spouse-partner',
      'fiance',
      'fiancé',
      'divorced',
      'ex_spouse',
      'ex-spouse',
      'deceased_spouse',
      'deceased-spouse',
    ].includes(normalized)
  ) {
    return 'spouse-partner';
  }

  if (
    normalized === 'coparent' ||
    normalized === 'co-parent'
  ) {
    return 'co-parent';
  }

  if (
    normalized === 'step_parent' ||
    normalized === 'step-parent' ||
    normalized === 'step_family' ||
    normalized === 'step-family'
  ) {
    return 'step-family';
  }

  if (
    normalized === 'in_law' ||
    normalized === 'in-law'
  ) {
    return 'in-law';
  }

  if (
    normalized === 'guardian'
  ) {
    return 'guardian';
  }

  if (
    normalized === 'foster_family' ||
    normalized === 'foster-family'
  ) {
    return 'foster-family';
  }

  if (
    normalized === 'chosen_family' ||
    normalized === 'chosen-family'
  ) {
    return 'chosen-family';
  }

  if (
    normalized === 'god_family' ||
    normalized === 'god-family'
  ) {
    return 'god-family';
  }

  if (
    normalized === 'affiliated_family' ||
    normalized === 'affiliated-family'
  ) {
    return 'affiliated-family';
  }

  if (normalized === 'sibling') {
    return 'sibling';
  }

  if (
    normalized === 'parent_child' ||
    normalized === 'parent-child'
  ) {
    return 'parent-child';
  }

  return null;
};

const adaptRelationshipHistory = (
  records: readonly RelationshipHistoryEntry[],
  memberById: ReadonlyMap<string, GraphMember>,
  unresolvedEvidence: CanonicalCompatibilityEvidence[],
): CanonicalTreeRelationshipEdge[] => {
  const edges: CanonicalTreeRelationshipEdge[] = [];

  for (const record of records) {
    const personA = memberById.get(record.personAId);
    const personB = memberById.get(record.personBId);

    if (!personA || !personB) {
      unresolvedEvidence.push({
        source: 'relationship_history',
        sourceReferenceId: record.id || null,
        memberIds: Object.freeze([
          record.personAId,
          record.personBId,
        ]),
        reason:
          'Relationship history references an endpoint that is not present in the loaded graph.',
      });
      continue;
    }

    if (personA.id === personB.id) {
      unresolvedEvidence.push({
        source: 'relationship_history',
        sourceReferenceId: record.id || null,
        memberIds: Object.freeze([personA.id]),
        reason:
          'Relationship history contains a self-reference.',
      });
      continue;
    }

    const kind =
      historyKindToCanonicalKind(
        record.relationshipKind,
      );

    if (!kind) {
      unresolvedEvidence.push({
        source: 'relationship_history',
        sourceReferenceId: record.id || null,
        memberIds: Object.freeze([
          personA.id,
          personB.id,
        ]),
        reason:
          `Relationship history kind ${record.relationshipKind} is not recognized by the canonical edge contract.`,
      });
      continue;
    }

    const sharedChildIds =
      sharedChildIdsFromMetadata(record.metadata);

    if (
      (
        kind === 'co-parent' ||
        kind === 'in-law'
      ) &&
      sharedChildIds.length === 0
    ) {
      unresolvedEvidence.push({
        source: 'relationship_history',
        sourceReferenceId: record.id || null,
        memberIds: Object.freeze([
          personA.id,
          personB.id,
        ]),
        reason:
          `Relationship history kind ${kind} requires governed shared-child connector evidence.`,
      });
      continue;
    }

    const status =
      statusFromText(
        record.status,
        record.endedAt === null,
      );

    const direction =
      kind === 'co-parent' ||
      kind === 'in-law'
        ? 'child-mediated'
        : kind === 'sibling' ||
            kind === 'spouse-partner' ||
            kind === 'chosen-family' ||
            kind === 'god-family'
          ? 'symmetric'
          : 'directed';

    edges.push(
      freezeEdge({
        id:
          record.id ||
          `relationship_history:${personA.id}:${personB.id}:${kind}`,

        ownerUserId:
          record.ownerUserId ??
          personA.ownerUserId ??
          personB.ownerUserId ??
          null,

        sourceMemberId:
          personA.id,

        targetMemberId:
          personB.id,

        kind,
        direction,
        status,

        sharedChildIds,

        createsAncestry:
          kind === 'parent-child',

        inheritsGeneration:
          kind !== 'guardian' &&
          kind !== 'foster-family',

        inheritsBranch:
          [
            'parent-child',
            'sibling',
            'spouse-partner',
            'co-parent',
            'step-family',
            'in-law',
          ].includes(kind),

        inheritsSide:
          [
            'parent-child',
            'sibling',
            'spouse-partner',
            'co-parent',
            'step-family',
            'in-law',
          ].includes(kind),

        createsInLawship:
          kind === 'spouse-partner' ||
          kind === 'in-law',

        rendersActivePartnership:
          kind === 'spouse-partner' &&
          status === 'active',

        affiliatedBranch:
          [
            'in-law',
            'guardian',
            'foster-family',
            'chosen-family',
            'god-family',
            'affiliated-family',
          ].includes(kind),

        startedAt:
          record.startedAt,

        endedAt:
          record.endedAt,

        confirmation:
          'confirmed',

        visibility:
          'family',

        preferredFamilyTitle:
          null,

        source:
          'relationship_history',

        sourceReferenceId:
          record.id || null,

        metadata: {
          ...record.metadata,
          relationshipKind:
            record.relationshipKind,
          rawStatus:
            record.status,
          notes:
            record.notes,
          createdAt:
            record.createdAt,
        },
      }),
    );
  }

  return edges;
};

export const projectCanonicalTreeCompatibilityEdges = (
  input: CanonicalTreeCompatibilityProjectionInput,
): CanonicalTreeCompatibilityProjection => {
  const members =
    [...input.members];

  const spousePartnerEdges =
    [...(input.spousePartnerEdges ?? [])];

  const relationshipHistory =
    [...(input.relationshipHistory ?? [])];

  const memberById =
    new Map(
      members.map(
        member =>
          [member.id, member] as const,
      ),
    );

  const invalidEvidence:
    CanonicalCompatibilityEvidence[] = [];

  const unresolvedEvidence:
    CanonicalCompatibilityEvidence[] = [];

  const workflowProjection =
    adaptRelationshipWorkflowsToCanonicalEdges(
      members,
    );

  for (
    const memberId of
    workflowProjection.invalidMemberIds
  ) {
    invalidEvidence.push({
      source:
        'relationship_workflow',
      sourceReferenceId:
        memberId,
      memberIds:
        Object.freeze([memberId]),
      reason:
        'Relationship workflow produced an invalid canonical edge.',
    });
  }

  for (
    const memberId of
    workflowProjection.unresolvedMemberIds
  ) {
    unresolvedEvidence.push({
      source:
        'relationship_workflow',
      sourceReferenceId:
        memberId,
      memberIds:
        Object.freeze([memberId]),
      reason:
        'Relationship workflow did not contain enough unique canonical evidence to emit an edge.',
    });
  }

  const candidates = [
    ...adaptParentIds(
      members,
      memberById,
      unresolvedEvidence,
    ),

    ...adaptCoParentsFromParentIds(
      members,
      memberById,
    ),

    ...adaptSpouseIds(
      members,
      memberById,
      unresolvedEvidence,
    ),

    ...workflowProjection.edges,

    ...adaptSpousePartnerEdges(
      spousePartnerEdges,
      memberById,
      unresolvedEvidence,
    ),

    ...adaptRelationshipHistory(
      relationshipHistory,
      memberById,
      unresolvedEvidence,
    ),
  ].sort(
    (left, right) =>
      sourcePriority[left.source] -
      sourcePriority[right.source],
  );

  const edges:
    CanonicalTreeRelationshipEdge[] = [];

  const seenIdentities =
    new Set<string>();

  const seenCoParentPairs =
    new Set<string>();

  let duplicateCount = 0;

  for (const edge of candidates) {
    const validation =
      validateCanonicalTreeRelationshipEdge(
        edge,
      );

    if (!validation.valid) {
      invalidEvidence.push({
        source:
          edge.source,
        sourceReferenceId:
          edge.sourceReferenceId,
        memberIds:
          Object.freeze([
            edge.sourceMemberId,
            edge.targetMemberId,
            ...edge.sharedChildIds,
          ]),
        reason:
          validation.issues
            .map(issue => issue.message)
            .join(' '),
      });

      continue;
    }

    if (edge.kind === 'co-parent') {
      const pairKey =
        canonicalTreeEdgePairKey(
          edge.sourceMemberId,
          edge.targetMemberId,
        );

      if (seenCoParentPairs.has(pairKey)) {
        duplicateCount += 1;
        continue;
      }

      seenCoParentPairs.add(pairKey);
    }

    const identity =
      compatibilityIdentityKey(edge);

    if (seenIdentities.has(identity)) {
      duplicateCount += 1;
      continue;
    }

    seenIdentities.add(identity);
    edges.push(edge);
  }

  return Object.freeze({
    edges:
      Object.freeze(edges),

    invalidEvidence:
      Object.freeze(invalidEvidence),

    unresolvedEvidence:
      Object.freeze(unresolvedEvidence),

    sourceCounts:
      Object.freeze({
        parentIds:
          candidates.filter(
            edge =>
              edge.source === 'parent_ids',
          ).length,

        spouseIds:
          candidates.filter(
            edge =>
              edge.source === 'spouse_id',
          ).length,

        relationshipWorkflows:
          candidates.filter(
            edge =>
              edge.source ===
              'relationship_workflow',
          ).length,

        spousePartnerEdges:
          candidates.filter(
            edge =>
              edge.source ===
              'spouse_partner_edges',
          ).length,

        relationshipHistory:
          candidates.filter(
            edge =>
              edge.source ===
              'relationship_history',
          ).length,
      }),

    duplicateCount,
  });
};
